// 2026-09-23 - FormuLearning - 진행 중 작업 전역 상태 (이슈 #82)
// 파일 위치: frontend/src/stores/busyStore.ts
// 오래 걸리는 작업이 **진행 중**이면 그 작업의 카드 밖 조작(다른 스텝·Save and continue·프로젝트 전환·
// Autosave 토글)을 잠근다. 작업 제어(스트림·중단 경합)는 소유 카드가 그대로 쥐고, 여기에는 잠금과
// 안내에 필요한 «요약»만 둔다. 동시 1건 전제 — 소유 카드 외 조작이 모두 잠기므로 두 번째 작업은
// 시작될 길이 없다. 근거: `ai-agent/claude/260923_#82_진행중작업공통잠금/검토_260923_진행중작업공통잠금.md`

import { create } from 'zustand'
import { toast } from './toastStore'

export interface BusyProgress {
  done: number
  total: number
  doing: string
}

export interface BusyTask {
  /** 짝 맞춤용 식별자(Step 1 은 검색 ID). 늦게 온 `end` 가 다음 작업을 풀지 않게 한다. */
  id: string
  /** 소유 스텝 — 이 카드만 잠금에서 빠진다. */
  step: number
  /** 안내 문구의 주어. 예: «Step 1 검색» */
  label: string
  progress?: BusyProgress
  phase: 'running' | 'breaking'
  /** 중단 수단(확인창 포함). 없으면 헤더에 Break 를 두지 않는다 — 중단할 수 없는 작업도 잠금은 건다(#83). */
  cancel?: () => void
}

interface BusyState {
  task: BusyTask | null
  begin: (task: Omit<BusyTask, 'phase'> & { phase?: BusyTask['phase'] }) => void
  update: (id: string, patch: Partial<Pick<BusyTask, 'progress' | 'phase' | 'cancel'>>) => void
  end: (id: string) => void
}

export const useBusyStore = create<BusyState>((set, get) => ({
  task: null,
  begin: (task) => {
    const cur = get().task
    if (cur && cur.id !== task.id) {
      // 잠금이 제대로 걸렸다면 생길 수 없다. 앞 작업을 덮으면 그 작업의 end 가 새 작업을 못 풀게 되므로 거부한다.
      console.error(`[busy] ${task.label} 시작 거부 — ${cur.label} 진행 중`)
      return
    }
    set({ task: { phase: 'running', ...task } })
  },
  update: (id, patch) => {
    const cur = get().task
    if (cur?.id === id) set({ task: { ...cur, ...patch } })
  },
  end: (id) => {
    if (get().task?.id === id) set({ task: null })
  },
}))

/** 안내 문구 — «Step 1 검색 작업이 진행 중입니다. 완료 후 누르거나 Break 로 중지하세요.»
 *  label 뒤에 «작업이» 를 붙여 받침에 따른 조사(이/가)를 고르지 않아도 되게 한다. */
export function busyMessage(task: BusyTask): string {
  return `${task.label} 작업이 진행 중입니다. 완료 후 누르거나 ${task.cancel ? 'Break 로 중지하세요' : '잠시 기다려 주세요'}.`
}

/**
 * 카드 밖 진입점의 가드. 진행 중이면 안내를 띄우고 true(=막음)를 돌려준다.
 * `allowStep` 은 소유 카드 자신의 조작을 통과시킬 때 쓴다.
 */
export function blockIfBusy(allowStep?: number): boolean {
  const task = useBusyStore.getState().task
  if (!task || (allowStep !== undefined && task.step === allowStep)) return false
  toast(busyMessage(task), 'warn')
  return true
}
